import React from "react";
import { X } from "lucide-react";
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  Background,
  useReactFlow,
} from "@xyflow/react";

import { StarNode } from "../../floor-planner/nodes/StarNode";
import { ArrowNode } from "../../floor-planner/nodes/ArrowNode";
import { LineNode } from "../../floor-planner/nodes/LineNode";
import { TriangleNode } from "../../floor-planner/nodes/TriangleNode";
import { CircleNode } from "../../floor-planner/nodes/CircleNode";
import { HexagonNode } from "../../floor-planner/nodes/HexagonNode";
import { PentagonNode } from "../../floor-planner/nodes/PentagonNode";
import { RectangleNode } from "../../floor-planner/nodes/RectangleNode";

const internalNodeTypes = {
  star: StarNode,
  arrow: ArrowNode,
  line: LineNode,
  triangle: TriangleNode,
  circle: CircleNode,
  hexagon: HexagonNode,
  pentagon: PentagonNode,
  rectangle: RectangleNode,
};

const FloorMapContent = ({ 
  previewNodes, 
  nodeTypes, 
  selectedFloorData,
  setSelectedBooth,
  setIsBoothPanelOpen,
  onClose
}) => {
  const { fitView, fitBounds } = useReactFlow();

  React.useEffect(() => {
    if (!selectedFloorData) return;

    const timer = setTimeout(() => {
      const focusAreas = selectedFloorData.focus_areas || [];
      const mainFocus = focusAreas.find(f => f.status === 'active') || focusAreas[0];

      if (mainFocus) {
        fitBounds({
          x: mainFocus.focus_x,
          y: mainFocus.focus_y,
          width: mainFocus.focus_width,
          height: mainFocus.focus_height,
        }, { padding: 0.1, duration: 800 });
      } else {
        fitView({ padding: 0.2, duration: 800 });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [selectedFloorData?.id, fitView, fitBounds]);

  return (
    <ReactFlow
      nodes={previewNodes}
      edges={[]}
      nodeTypes={nodeTypes}
      panOnScroll
      zoomOnScroll
      panOnDrag
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={true}
      onNodeClick={(e, node) => {
        if (node.type === "booth" && (node.data?.status === "available" || !node.data?.status)) {
          setSelectedBooth(node);
          setIsBoothPanelOpen(true);
          onClose(); // Close modal to show the details sidebar
        }
      }}
      minZoom={0.2}
      maxZoom={4}
    >
      <Background color="#ccc" gap={20} />
      <Controls showInteractive={false} position="bottom-right" />
    </ReactFlow>
  );
};

export default function FullPagePreviewModal({
  isOpen,
  onClose,
  availableBooths,
  bookedBooths,
  onHoldBooths,
  selectedFloor,
  setSelectedFloor,
  floors,
  previewNodes,
  nodeTypes,
  setSelectedBooth,
  setIsBoothPanelOpen,
  eventSettings,
}) {
  const selectedFloorData = React.useMemo(
    () => floors.find((f) => f.id === selectedFloor),
    [floors, selectedFloor],
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[#6ebf8b]">
      <div className="relative px-6 py-4 md:px-8 md:py-6 text-white shrink-0">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 md:top-6 md:right-6 p-2 rounded-full bg-black/10 hover:bg-black/20 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
        <h1 className="text-2xl md:text-[28px] font-bold mb-1">Floor Plan</h1>
        <p className="text-[13px] md:text-[15px] opacity-90">
          Refer to the floor plan to view the layout of the event
        </p>
      </div>
      <div className="flex-1 p-4 md:p-6 pt-0 overflow-hidden flex flex-col">
        <div className="flex-1 bg-white rounded-lg shadow-xl overflow-hidden flex flex-col">
          <div className="px-4 py-3 md:px-6 md:py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 bg-[#fbfcfd]">
            <div className="flex flex-wrap items-center gap-4 md:gap-6">
              <div className="flex items-center gap-2">
                <div 
                  className="w-3.5 h-3.5 rounded-full border border-gray-300" 
                  style={{ backgroundColor: eventSettings?.booth_color_available || "#ffffff" }}
                />
                <span className="text-sm font-medium">
                  Available ({availableBooths})
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div 
                  className="w-3.5 h-3.5 rounded-full" 
                  style={{ backgroundColor: eventSettings?.booth_color_assigned || "#FF69B4" }}
                />
                <span className="text-sm font-medium">
                  Booked ({bookedBooths})
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div 
                  className="w-3.5 h-3.5 rounded-full" 
                  style={{ backgroundColor: eventSettings?.booth_color_reserved || "#FFB6C1" }}
                />
                <span className="text-sm font-medium">
                  On Hold ({onHoldBooths})
                </span>
              </div>
              
            </div>
            <select
              value={selectedFloor}
              onChange={(e) => setSelectedFloor(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {floors.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 relative">
            <ReactFlowProvider>
              <FloorMapContent
                previewNodes={previewNodes}
                nodeTypes={{...nodeTypes, ...internalNodeTypes}}
                selectedFloorData={selectedFloorData}
                setSelectedBooth={setSelectedBooth}
                setIsBoothPanelOpen={setIsBoothPanelOpen}
                onClose={onClose}
              />
            </ReactFlowProvider>
          </div>
        </div>
      </div>
    </div>
  );
}
